import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {X, Check, ArrowRight} from 'lucide-react'

const Comparison = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const [activeTab, setActiveTab] = useState<'traditional' | 'ewpass'>('traditional')

  const traditional = [
    { text: 'Compra pontual', icon: X, color: 'red' },
    { text: 'Conteúdo acumulado', icon: X, color: 'red' },
    { text: 'Estudo solitário', icon: X, color: 'red' },
    { text: 'Abandono silencioso', icon: X, color: 'red' },
  ]

  const ewpass = [
    { text: 'Assinatura contínua', icon: Check, color: 'green' },
    { text: 'Conteúdo + prática guiada', icon: Check, color: 'green' },
    { text: 'Professores + IA', icon: Check, color: 'green' },
    { text: 'Evolução com constância', icon: Check, color: 'green' },
  ]

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-[#0D7FA3] relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-0 left-0 w-96 h-96 bg-[#C64DAD] rounded-full blur-3xl"
      />
      <motion.div
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute bottom-0 right-0 w-96 h-96 bg-[#0D7FA3] rounded-full blur-3xl"
      />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Aprender inglês não é um{' '}
              <span className="line-through decoration-red-400 decoration-4">projeto</span>
              <br />
              É um <span className="bg-gradient-to-r from-yellow-300 to-yellow-400 bg-clip-text text-transparent">ambiente</span>
            </h2>
          </motion.div>

          {/* Mobile toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:hidden flex gap-2 mb-8 bg-white/10 backdrop-blur-sm rounded-full p-1"
          >
            <button
              onClick={() => setActiveTab('traditional')}
              className={`flex-1 py-3 px-4 rounded-full font-semibold transition-all duration-300 ${
                activeTab === 'traditional'
                  ? 'bg-red-500 text-white'
                  : 'text-white/70'
              }`}
            >
              Modelo Tradicional
            </button>
            <button
              onClick={() => setActiveTab('ewpass')}
              className={`flex-1 py-3 px-4 rounded-full font-semibold transition-all duration-300 ${
                activeTab === 'ewpass'
                  ? 'bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] text-white'
                  : 'text-white/70'
              }`}
            >
              EW Pass
            </button>
          </motion.div>

          {/* Desktop: Side by side */}
          <div className="hidden md:grid md:grid-cols-2 gap-8 mb-12">
            {/* Traditional */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="bg-white/5 backdrop-blur-md border-2 border-red-500/30 rounded-3xl p-8 hover:border-red-500/60 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-white" strokeWidth={3} />
                </div>
                <h3 className="text-2xl font-bold text-white">Modelo tradicional</h3>
              </div>
              <div className="space-y-4">
                {traditional.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="flex items-center gap-3 text-white/80"
                  >
                    <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span className="text-lg">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* EW Pass */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="bg-gradient-to-br from-[#0D7FA3]/20 to-[#C64DAD]/20 backdrop-blur-md border-2 border-[#0D7FA3] rounded-3xl p-8 relative overflow-hidden hover:border-[#C64DAD] transition-all duration-300 hover:scale-105"
            >
              <div className="absolute top-4 right-4">
                <div className="bg-yellow-300 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                  ✨ NOVO
                </div>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#0D7FA3] to-[#C64DAD] rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" strokeWidth={3} />
                </div>
                <h3 className="text-2xl font-bold text-white">EW Pass</h3>
              </div>
              <div className="space-y-4">
                {ewpass.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="flex items-center gap-3 text-white"
                  >
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-lg font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Mobile: Animated cards */}
          <div className="md:hidden mb-12">
            <AnimatePresence mode="wait">
              {activeTab === 'traditional' ? (
                <motion.div
                  key="traditional"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white/5 backdrop-blur-md border-2 border-red-500/30 rounded-3xl p-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                      <X className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                    <h3 className="text-xl font-bold text-white">Modelo tradicional</h3>
                  </div>
                  <div className="space-y-3">
                    {traditional.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 text-white/80">
                        <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="ewpass"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gradient-to-br from-[#0D7FA3]/20 to-[#C64DAD]/20 backdrop-blur-md border-2 border-[#0D7FA3] rounded-3xl p-6 relative"
                >
                  <div className="absolute top-4 right-4">
                    <div className="bg-yellow-300 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                      ✨ NOVO
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#0D7FA3] to-[#C64DAD] rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                    <h3 className="text-xl font-bold text-white">EW Pass</h3>
                  </div>
                  <div className="space-y-3">
                    {ewpass.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 text-white">
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                        <span className="font-medium">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Closing statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center"
          >
            <p className="text-2xl md:text-3xl text-white font-bold leading-relaxed">
              Você não compra um curso para{' '}
              <span className="line-through decoration-red-400 decoration-2">terminar</span>.
              <br />
              Você entra em um sistema para{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                continuar
              </span>
              .
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Comparison
